<template>
    <div class="profile-education-item" :class="`profile-education-item--${size}`">
        <VJoyIcon :size="size" name="school" class="profile-education-item__icon" :class="`profile-education-item__icon--${size}`" />
        <div class="profile-education-item__content">
            <div
                class="profile-education-item__content__title"
                :class="`profile-education-item__content__title--${size}`"
                data-testid="profile-education-item-title"
            >
                <span>{{ education.description }}</span>
            </div>
            <div class="profile-education-item__content__subtitle" data-testid="profile-education-item-school">
                {{ education.title }}
            </div>
            <VJoyWrapper
                v-if="education.graduationYear"
                class="profile-education-item__content__calendar"
                data-testid="profile-education-item-year"
                column-gap="2"
                no-margin
            >
                <small>{{ education.graduationYear }}</small>
            </VJoyWrapper>
            <div v-if="education.content" class="profile-education-item__content__text" data-testid="profile-education-item-description">
                {{ education.content }}
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import {VJoyWrapper, VJoyIcon} from '@maltjoy/core-vue';
    import type {EducationItem} from '~/components/_common/education/EducationItem';
    import {EducationSize} from '~/components/_common/education/EducationItem';

    export interface Props {
        size?: EducationSize;
        education: EducationItem;
    }

    withDefaults(defineProps<Props>(), {
        size: EducationSize.LARGE,
    });
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .profile-education-item {
        display: flex;

        &--large {
            @include mq.screen_xs() {
                flex-direction: column;
            }
        }

        &--small {
            @include mq.screen_xs() {
                flex-direction: row;
            }

            @include mq.screen_xxs() {
                flex-direction: column;
            }
        }

        &__icon {
            &--large {
                @include mq.screen_xs() {
                    display: none;
                }
            }

            &--small {
                @include mq.screen_xs() {
                    display: block;
                }

                @include mq.screen_xxs() {
                    display: none;
                }
            }
        }

        &__content {
            margin-left: var(--joy-core-spacing-4);
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            width: 100%;

            &__title {
                margin-bottom: var(--joy-core-spacing-1);
                font-family: var(--joy-font-family-base);
                font-weight: var(--joy-font-weight-bold);
                line-height: var(--joy-line-height-large);
                word-break: normal;
                overflow-wrap: anywhere;
                text-align: left;

                &--small {
                    font-size: var(--joy-font-size-primary-400);
                }

                &--large {
                    font-size: var(--joy-font-size-primary-600);
                }
            }

            &__subtitle {
                font-size: var(--joy-font-size-primary-300);
                margin-bottom: var(--joy-core-spacing-1);
                color: var(--joy-color-text-default);
                display: flex;
                word-break: normal;
                overflow-wrap: anywhere;
                text-align: left;
            }

            &__calendar {
                color: var(--joy-color-neutral-40);
                margin-bottom: var(--joy-core-spacing-1);
            }

            &__text {
                margin-top: var(--joy-core-spacing-3);
                white-space: pre-line;
                word-break: normal;
                overflow-wrap: anywhere;
                text-align: left;
            }
        }
    }
</style>
